'use client';

/**
 * Booking Request Form
 * Client component for submitting booking requests
 */

import React, { useEffect, useRef, useState } from 'react';
import { useAuthSession } from '@/app/components/auth/useAuthSession';
import type { Property, RatePlan } from '@/core/domain/entities';
import AvailabilityCalendar from './AvailabilityCalendar';
import { useRatePlanSelection } from './RatePlanProvider';
import { analytics } from '@/adapters/analytics';
import NoticeAtCollection from '@/app/components/legal/NoticeAtCollection';
import { strings } from '@/content/strings';
import { useAvailabilityContext } from './AvailabilityProvider';

interface BookingFormProps {
  property: Property;
  ratePlans?: RatePlan[];
  blocked?: { start: string; end: string; source: 'booking' | 'ical' | 'manual' }[];
  minNightOverrides?: { start: string; end: string; minNights: number }[];
}

export default function BookingForm({
  property,
  ratePlans = [],
  blocked = [],
  minNightOverrides = [],
}: BookingFormProps) {
  const availability = useAvailabilityContext();
  const blockedDates = availability?.blocked ?? blocked;
  const minNightOverridesState = availability?.minNightOverrides ?? minNightOverrides;
  const propertyStrings = strings.screens.property;
  const currencySymbol = strings.common.symbols.currency;
  const defaultAdults = Math.min(2, property.maxGuests);
  const [guestCounts, setGuestCounts] = useState({
    adults: defaultAdults,
    children: 0,
    infants: 0,
    pets: 0,
  });
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [specialRequests, setSpecialRequests] = useState('');
  const ratePlanSelection = useRatePlanSelection();
  const [localRatePlanId, setLocalRatePlanId] = useState(ratePlans[0]?.id ?? '');
  const selectedRatePlanId = ratePlanSelection?.selectedRatePlanId ?? localRatePlanId;
  const setSelectedRatePlanId = (nextId: string) => {
    ratePlanSelection?.setSelectedRatePlanId(nextId);
    setLocalRatePlanId(nextId);
  };
  const [quote, setQuote] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [rangeEventLoaded, setRangeEventLoaded] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const [defaultsLoaded, setDefaultsLoaded] = useState(false);
  const { data: session } = useAuthSession();
  const lockedEmail = !!session?.user?.email;
  const totalGuests = guestCounts.adults + guestCounts.children + guestCounts.infants;
  const formattedTaxRate = Number.isFinite(property.taxRate)
    ? `${property.taxRate}`
    : '0';
  const taxRateLabel =
    (property.taxRate ?? 0) > 0
      ? `${propertyStrings.taxes} (${formattedTaxRate}%)`
      : propertyStrings.taxes;
  const hasVariableRates =
    Array.isArray(quote?.pricing?.nightlyRates) &&
    quote.pricing.nightlyRates.length > 0 &&
    quote.pricing.nightlyRates.some((rate: number) => rate !== quote.pricing.nightlyRates[0]);
  const serviceFeeRate = property.serviceFeeRate ?? 0;
  const baseNightlyRateWithFee = Math.round(property.baseNightlyRate * (1 + serviceFeeRate / 100));
  const quotePricing = quote?.pricing;
  const guestSubtotal = quotePricing
    ? quotePricing.subtotal + (quotePricing.serviceFee ?? 0)
    : null;
  const discountAmount = quotePricing?.discount ?? 0;
  const hasDiscount = discountAmount > 0;
  const breakdownNightlyTotal = quotePricing
    ? (guestSubtotal ?? 0) + (hasDiscount ? discountAmount : 0)
    : null;
  const breakdownNightlyRate =
    quotePricing && quotePricing.nights > 0
      ? Math.round((breakdownNightlyTotal ?? 0) / quotePricing.nights)
      : baseNightlyRateWithFee;
  const displayedNightlyRate =
    quotePricing && quotePricing.nights > 0
      ? Math.round((guestSubtotal ?? 0) / quotePricing.nights)
      : baseNightlyRateWithFee;

  const planFromQuote = quote?.ratePlan as RatePlan | undefined;
  const activePlan =
    planFromQuote && planFromQuote.id === selectedRatePlanId
      ? planFromQuote
      : ratePlans.find((p) => p.id === selectedRatePlanId);
  const activeLeadDays = activePlan?.minLeadDays ?? 0;

  const paymentSummary = activePlan
    ? activePlan.paymentRule.type === 'full-prepay'
      ? propertyStrings.paymentSummaryFullPrepay
      : activePlan.paymentRule.type === 'pay-at-checkin'
        ? propertyStrings.paymentSummaryPayAtCheckIn
        : propertyStrings.paymentSummaryDeposit(
            Math.round(activePlan.paymentRule.depositPercent * 100),
            activePlan.paymentRule.remainderDaysBeforeCheckIn
          )
    : propertyStrings.paymentSummaryDefault;

  const cancellationSummary = activePlan
    ? activePlan.refundLadder.length === 0
      ? propertyStrings.cancellationSummaryNonRefundable
      : (() => {
          const top = [...activePlan.refundLadder].sort(
            (a, b) => b.cutoffDaysBeforeCheckIn - a.cutoffDaysBeforeCheckIn
          )[0];
          const pct = Math.round(top.refundPercent * 100);
          return pct >= 100
            ? propertyStrings.cancellationSummaryFreeCancel(top.cutoffDaysBeforeCheckIn)
            : propertyStrings.cancellationSummaryPercent(pct, top.cutoffDaysBeforeCheckIn);
        })()
    : propertyStrings.cancellationSummaryDefault;

  useEffect(() => {
    if (!session?.user?.email) return;
    setEmail(session.user.email);
  }, [session?.user?.email]);

  // Calculate quote when dates change
  const calculateQuote = async () => {
    if (!checkIn || !checkOut) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          propertyId: property.id,
          ratePlanId: selectedRatePlanId || undefined,
          checkIn,
          checkOut,
          numberOfGuests: totalGuests,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setQuote(data.data);
      } else {
        setError(data.errors?.join(', ') || data.error);
        setQuote(null);
      }
    } catch (err) {
      setError(propertyStrings.calculateQuoteError);
      setQuote(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const response = await fetch('/api/bookings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          propertyId: property.id,
          ratePlanId: selectedRatePlanId || undefined,
          guest: { firstName, lastName, email, phone },
          numberOfGuests: totalGuests,
          checkIn,
          checkOut,
          specialRequests,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        analytics.track('booking_request_submitted', {
          propertyId: property.id,
          ratePlanId: selectedRatePlanId || 'default',
        });
        // Reset form
        setFirstName('');
        setLastName('');
        setEmail('');
        setPhone('');
        setSpecialRequests('');
      } else {
        setError(data.errors?.join(', ') || data.error);
      }
    } catch (err) {
      setError(propertyStrings.submitBookingError);
    } finally {
      setLoading(false);
    }
  };

  // Listen for range selection events from AvailabilityCalendar
  React.useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (!detail) return;
      if (detail.checkIn) setCheckIn(detail.checkIn);
      if (detail.checkOut) setCheckOut(detail.checkOut);
      setRangeEventLoaded(true);
    };
    window.addEventListener('bookingRangeSelected', handler as EventListener);
    return () => window.removeEventListener('bookingRangeSelected', handler as EventListener);
  }, []);

  // Recalculate quote when range is set via calendar
  const quoteTimerRef = useRef<number | null>(null);
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const media = window.matchMedia('(min-width: 1024px)');
    const update = () => setIsDesktop(media.matches);
    update();
    if (media.addEventListener) {
      media.addEventListener('change', update);
      return () => media.removeEventListener('change', update);
    }
    media.addListener(update);
    return () => media.removeListener(update);
  }, []);

  const scheduleQuote = (delay = 250) => {
    if (!checkIn || !checkOut) return;
    if (!isDesktop) return;
    if (quoteTimerRef.current) window.clearTimeout(quoteTimerRef.current);
    quoteTimerRef.current = window.setTimeout(() => {
      void calculateQuote();
    }, delay);
  };

  React.useEffect(() => {
    if (rangeEventLoaded && checkIn && checkOut) {
      scheduleQuote(0);
      setRangeEventLoaded(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeEventLoaded, checkIn, checkOut, isDesktop]);

  useEffect(() => {
    if (checkIn && checkOut) {
      scheduleQuote();
    }
  }, [totalGuests, checkIn, checkOut, selectedRatePlanId, isDesktop]);

  useEffect(() => {
    if (defaultsLoaded) return;
    if (!checkIn && !checkOut) {
      const today = new Date();
      const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      if (activeLeadDays > 0) {
        start.setDate(start.getDate() + activeLeadDays);
      }
      const end = new Date(start);
      end.setDate(end.getDate() + 5);
      setCheckIn(dateKeyLocal(start));
      setCheckOut(dateKeyLocal(end));
      setDefaultsLoaded(true);
      scheduleQuote(200);
      return;
    }
    setDefaultsLoaded(true);
  }, [defaultsLoaded, checkIn, checkOut, activeLeadDays]);

  useEffect(() => {
    if (!showCalendar) return;
    const onClickOutside = (event: MouseEvent) => {
      if (!pickerRef.current) return;
      if (!pickerRef.current.contains(event.target as Node)) {
        setShowCalendar(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShowCalendar(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showCalendar]);

  const updateGuestCount = (key: 'adults' | 'children' | 'infants' | 'pets', delta: number) => {
    setGuestCounts((prev) => {
      const min = key === 'adults' ? 1 : 0;
      const nextValue = prev[key] + delta;
      if (nextValue < min) return prev;

      if (key !== 'pets') {
        const nextTotal = prev.adults + prev.children + prev.infants + delta;
        if (nextTotal > property.maxGuests) return prev;
      } else {
        const maxPets = property.maxPets ?? 0;
        if (maxPets === 0) return prev;
        if (nextValue > maxPets) return prev;
      }

      return { ...prev, [key]: nextValue };
    });

  };

  return (
    <div className="space-y-4">
      {/* Pricing Header */}
      <div className="mb-6 pb-6 border-b border-border">
        <div className="flex items-baseline">
          <span className="text-3xl font-bold text-foreground">
            ${(displayedNightlyRate / 100).toFixed(2)}
          </span>
          <span className="text-muted-foreground ml-2">{propertyStrings.perNight}</span>
        </div>
        {ratePlans && ratePlans.length > 0 && (
          <div className="mt-4 space-y-2">
            <div className="text-sm font-semibold text-foreground">{propertyStrings.ratePlanHeader}</div>
            <div className="grid grid-cols-1 gap-2">
              {ratePlans.map((plan) => (
                <label
                  key={plan.id}
                  className={`border rounded-lg px-3 py-2 cursor-pointer transition-colors ${
                    selectedRatePlanId === plan.id
                      ? 'border-primary bg-accent/30'
                      : 'border-border hover:border-primary/60'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="text-sm font-semibold text-foreground">{plan.name}</div>
                      {plan.description && <div className="text-xs text-muted-foreground">{plan.description}</div>}
                    </div>
                    <input
                      type="radio"
                      name="rate-plan"
                      value={plan.id}
                      checked={selectedRatePlanId === plan.id}
                      onChange={(e) => {
                        setSelectedRatePlanId(e.target.value);
                      }}
                      className="text-primary focus:ring-primary"
                    />
                  </div>
                  <div className="mt-1 flex flex-wrap gap-2 text-xs text-muted-foreground">
                    {plan.paymentRule.type === 'full-prepay' && (
                      <span className="px-2 py-1 bg-card rounded border border-border">
                        {propertyStrings.fullPrepay}
                      </span>
                    )}
                    {plan.paymentRule.type === 'deposit' && (
                      <span className="px-2 py-1 bg-card rounded border border-border">
                        {propertyStrings.depositLabel(
                          Math.round(plan.paymentRule.depositPercent * 100),
                          plan.paymentRule.remainderDaysBeforeCheckIn
                        )}
                      </span>
                    )}
                    {plan.paymentRule.type === 'pay-at-checkin' && (
                      <span className="px-2 py-1 bg-card rounded border border-border">
                        {propertyStrings.payAtCheckIn}
                      </span>
                    )}
                    {plan.refundLadder.length > 0 && (
                      <span className="px-2 py-1 bg-card rounded border border-border">
                        {propertyStrings.freeCancelUntilDays(plan.cancellationWindowDays)}
                      </span>
                    )}
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      {success ? (
        <div className="bg-accent/30 border border-border text-foreground rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <svg className="w-6 h-6 text-primary mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <h3 className="font-semibold mb-1">{propertyStrings.bookingRequestTitle}</h3>
              <p className="text-sm">
                {propertyStrings.bookingRequestCopy}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Dates */}
          <div className="space-y-3" ref={pickerRef}>
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-foreground">
                {propertyStrings.selectDatesTitle}
              </label>
              {(checkIn || checkOut) && (
                <button
                  type="button"
                  onClick={() => {
                    setCheckIn('');
                    setCheckOut('');
                    setQuote(null);
                  }}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  {propertyStrings.clearDates}
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-muted-foreground mb-1">
                  {propertyStrings.checkInLabel}
                </label>
                <button
                  type="button"
                  onClick={() => setShowCalendar(true)}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-ghost text-foreground text-left"
                >
                  {checkIn || propertyStrings.datePlaceholder}
                </button>
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">
                  {propertyStrings.checkOutLabel}
                </label>
                <button
                  type="button"
                  onClick={() => setShowCalendar(true)}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-ghost text-foreground text-left"
                >
                  {checkOut || propertyStrings.datePlaceholder}
                </button>
              </div>
            </div>
            {showCalendar && (
              <div className="relative">
                <div className="absolute left-0 right-0 mt-2 z-20 rounded-2xl border border-border bg-card shadow-lg p-3">
                  <AvailabilityCalendar
                    blocked={blockedDates}
                    minNights={property.minNights}
                    maxNights={property.maxNights}
                    minLeadDays={activeLeadDays}
                    minNightOverrides={minNightOverridesState}
                    showMinNightHint
                    showLegend={false}
                    monthsToShow={1}
                    layout="stacked"
                    onRangeSelected={({ checkIn: ci, checkOut: co }) => {
                      setCheckIn(ci);
                      setCheckOut(co);
                      setShowCalendar(false);
                      setTimeout(calculateQuote, 100);
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Guests */}
          <div>
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-foreground">
                {propertyStrings.guestsLabel}
              </label>
              <span className="text-xs text-muted-foreground">
                {propertyStrings.guestsCount(totalGuests, property.maxGuests)}
              </span>
            </div>
            <div className="mt-2 space-y-2 rounded-lg border border-border bg-card p-3">
              {[
                {
                  key: 'adults',
                  label: propertyStrings.guests.adults,
                  hint: propertyStrings.guests.adultsHint,
                  min: 1,
                },
                {
                  key: 'children',
                  label: propertyStrings.guests.children,
                  hint: propertyStrings.guests.childrenHint,
                  min: 0,
                },
                {
                  key: 'infants',
                  label: propertyStrings.guests.infants,
                  hint: propertyStrings.guests.infantsHint,
                  min: 0,
                },
                ...(property.maxPets && property.maxPets > 0
                  ? [{
                      key: 'pets',
                      label: propertyStrings.guests.pets,
                      hint: propertyStrings.guests.petsHint,
                      min: 0,
                    }]
                  : []),
              ].map((row) => {
                const value = guestCounts[row.key as keyof typeof guestCounts] as number;
                const atMin = value <= row.min;
                const atMax =
                  row.key === 'pets'
                    ? value >= (property.maxPets ?? 0)
                    : totalGuests >= property.maxGuests;

                return (
                  <div key={row.key} className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-foreground">{row.label}</div>
                      <div className="text-xs text-muted-foreground">{row.hint}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => updateGuestCount(row.key as any, -1)}
                        disabled={atMin}
                        className="h-8 w-8 rounded-full border border-border bg-ghost text-foreground disabled:opacity-40"
                        aria-label={propertyStrings.guestControlDecrease(row.label)}
                      >
                        −
                      </button>
                      <div className="w-6 text-center text-sm font-semibold text-foreground">
                        {value}
                      </div>
                      <button
                        type="button"
                        onClick={() => updateGuestCount(row.key as any, 1)}
                        disabled={atMax}
                        className="h-8 w-8 rounded-full border border-border bg-ghost text-foreground disabled:opacity-40"
                        aria-label={propertyStrings.guestControlIncrease(row.label)}
                      >
                        +
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Quote Display */}
          {quote && (
            <div className="bg-accent/30 border border-border rounded-lg p-4 space-y-2">
              <h3 className="font-semibold text-foreground mb-3">{propertyStrings.priceBreakdownTitle}</h3>
              <div className="space-y-2 text-sm text-foreground">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {hasVariableRates ? propertyStrings.averageNightlyRate : propertyStrings.nightlyRate} {currencySymbol}{(
                      breakdownNightlyRate / 100
                    ).toFixed(2)} × {quote.pricing.nights} nights
                  </span>
                  <span className="text-foreground">{currencySymbol}{((breakdownNightlyTotal ?? 0) / 100).toFixed(2)}</span>
                </div>
                {hasDiscount && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{propertyStrings.discount}</span>
                    <span className="text-foreground">-{currencySymbol}{(discountAmount / 100).toFixed(2)}</span>
                  </div>
                )}
                {quote.pricing.cleaningFee > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{propertyStrings.cleaningFee}</span>
                    <span className="text-foreground">{currencySymbol}{(quote.pricing.cleaningFee / 100).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{taxRateLabel}</span>
                  <span className="text-foreground">{currencySymbol}{(quote.pricing.taxes / 100).toFixed(2)}</span>
                </div>
                <div className="border-t border-border pt-2 flex justify-between font-bold">
                  <span className="text-foreground">{propertyStrings.total}</span>
                  <span className="text-foreground">{currencySymbol}{(quote.pricing.total / 100).toFixed(2)}</span>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground space-y-1">
                <p className="font-semibold text-foreground mb-1">
                  {activePlan ? activePlan.name : propertyStrings.paymentCancellation}
                </p>
                <p>{paymentSummary}</p>
                <p className="mt-1">{cancellationSummary}</p>
              </div>
            </div>
          )}

          {/* Guest Information */}
          <div className="pt-4 border-t border-border">
            <h3 className="font-semibold text-foreground mb-3">{propertyStrings.guestInfoTitle}</h3>
            
            <div className="grid grid-cols-2 gap-3 mb-3">
              <input
                type="text"
                required
                placeholder={propertyStrings.firstNamePlaceholder}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary"
              />
              <input
                type="text"
                required
                placeholder={propertyStrings.lastNamePlaceholder}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>

            <input
              type="email"
              required
              placeholder={propertyStrings.emailPlaceholder}
              value={email}
              onChange={(e) => {
                if (!lockedEmail) setEmail(e.target.value);
              }}
              readOnly={lockedEmail}
              aria-readonly={lockedEmail}
              className={`w-full px-3 py-2 border border-border rounded-lg text-foreground focus:ring-2 focus:ring-primary focus:border-primary mb-3 ${
                lockedEmail ? 'bg-ghost/70 text-muted-foreground' : 'bg-background'
              }`}
            />

            <input
              type="tel"
              required
              placeholder={propertyStrings.phonePlaceholder}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary mb-3"
            />

            <textarea
              placeholder={propertyStrings.specialRequests}
              value={specialRequests}
              onChange={(e) => setSpecialRequests(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary"
            />
            <div className="mt-3">
              <NoticeAtCollection />
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-rose-700 text-sm">
              {error}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || !quote}
            className={`w-full py-3 px-4 rounded-lg font-semibold text-primary-foreground transition-colors ${
              loading || !quote
                ? 'bg-muted cursor-not-allowed text-muted-foreground'
                : 'bg-primary hover:opacity-90 active:opacity-100'
            }`}
          >
            {loading ? propertyStrings.processing : propertyStrings.requestToBook}
          </button>

          <p className="text-xs text-muted-foreground text-center">
            {propertyStrings.bookingHelper}
          </p>
        </form>
      )}
    </div>
  );
}

function dateKeyLocal(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
